import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['modal', 'slider', 'counter', 'previousBtn', 'nextBtn', 'image', 'loader']
  static values = {
    currentIndex: { type: Number, default: 0 },
    totalPhotos: { type: Number, default: 0 }
  }

  connect() {
    this.updateCounter()
    this.updateButtonsVisibility()
    this.boundHandleScroll = this.handleScroll.bind(this)
    this.imagesLoaded = false
  }

  open(event) {
    const index = event?.params?.index || 0
    this.modalTarget.classList.remove('opacity-0', 'pointer-events-none')
    this.modalTarget.classList.add('opacity-100')
    document.body.style.overflow = 'hidden'
    this.currentIndexValue = index
    const slideWidth = this.sliderTarget.clientWidth
    this.sliderTarget.scrollLeft = slideWidth * index
    this.updateCounter()
    this.sliderTarget.addEventListener('scroll', this.boundHandleScroll)

    if (!this.imagesLoaded) {
      setTimeout(() => {
        this.loadImages()
        this.imagesLoaded = true
      }, 300)
    }
  }

  loadImages() {
    this.imageTargets.forEach((imgElement, index) => {
      const dataSrc = imgElement.getAttribute('data-src')
      if (dataSrc && !imgElement.src.startsWith('http')) {
        imgElement.src = dataSrc
        imgElement.removeAttribute('data-src')
        
        imgElement.addEventListener('load', () => {
          imgElement.classList.remove('opacity-0')
          imgElement.classList.add('opacity-100', 'transition-opacity', 'duration-300')
          if (this.loaderTargets[index]) {
            this.loaderTargets[index].classList.add('hidden')
          }
        })
      }

      // Add click to toggle zoom
      imgElement.addEventListener('click', (e) => this.toggleZoom(e, imgElement))
    })
  }

  toggleZoom(event, imgElement) {
    event.stopPropagation()
    
    const isZoomed = imgElement.classList.contains('zoomed')
    
    if (isZoomed) {
      // Reset zoom
      imgElement.style.transform = 'scale(1)'
      imgElement.style.transformOrigin = 'center'
      imgElement.classList.remove('zoomed', 'cursor-zoom-out')
      imgElement.classList.add('cursor-zoom-in')
    } else {
      // Calculate click position relative to the image
      const rect = imgElement.getBoundingClientRect()
      const x = ((event.clientX - rect.left) / rect.width) * 100
      const y = ((event.clientY - rect.top) / rect.height) * 100
      
      // Set transform origin to the click position
      imgElement.style.transformOrigin = `${x}% ${y}%`
      
      // Apply zoom
      imgElement.style.transform = 'scale(3)'
      imgElement.classList.remove('cursor-zoom-in')
      imgElement.classList.add('zoomed', 'cursor-zoom-out')
    }
  }

  close() {
    this.modalTarget.classList.remove('opacity-100')
    this.modalTarget.classList.add('opacity-0', 'pointer-events-none')
    document.body.style.overflow = 'auto'
    this.sliderTarget.removeEventListener('scroll', this.boundHandleScroll)
    
    // Reset all zooms when closing
    this.imageTargets.forEach((imgElement) => {
      imgElement.style.transform = 'scale(1)'
      imgElement.style.transformOrigin = 'center'
      imgElement.classList.remove('zoomed', 'cursor-zoom-out')
      imgElement.classList.add('cursor-zoom-in')
    })
  }

  handleScroll() {
    clearTimeout(this.scrollTimeout)
    this.scrollTimeout = setTimeout(() => {
      const slideWidth = this.sliderTarget.clientWidth
      const newIndex = Math.round(this.sliderTarget.scrollLeft / slideWidth)
      if (newIndex !== this.currentIndexValue) {
        this.currentIndexValue = newIndex
        this.updateCounter()
      }
    }, 100)
  }

  next() {
    if (this.currentIndexValue < this.totalPhotosValue - 1) {
      this.currentIndexValue++
      this.scrollToCurrentSlide()
    }
  }

  previous() {
    if (this.currentIndexValue > 0) {
      this.currentIndexValue--
      this.scrollToCurrentSlide()
    }
  }

  scrollToCurrentSlide() {
    const slideWidth = this.sliderTarget.clientWidth
    this.sliderTarget.scrollTo({
      left: slideWidth * this.currentIndexValue,
      behavior: 'smooth'
    })
    this.updateCounter()
  }

  updateCounter() {
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${this.currentIndexValue + 1} / ${this.totalPhotosValue}`
    }
    this.updateButtonsVisibility()
  }

  updateButtonsVisibility() {
    if (this.hasPreviousBtnTarget) {
      if (this.currentIndexValue === 0) {
        this.previousBtnTarget.classList.add('hidden')
      } else {
        this.previousBtnTarget.classList.remove('hidden')
      }
    }

    if (this.hasNextBtnTarget) {
      if (this.currentIndexValue === this.totalPhotosValue - 1) {
        this.nextBtnTarget.classList.add('hidden')
      } else {
        this.nextBtnTarget.classList.remove('hidden')
      }
    }
  }

  closeOnEscape(event) {
    if (event.key === 'Escape') {
      this.close()
    } else if (event.key === 'ArrowRight') {
      this.next()
    } else if (event.key === 'ArrowLeft') {
      this.previous()
    }
  }
}
